import { Dropdown } from 'react-bootstrap';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable'
import html2canvas from 'html2canvas';
import { HiDownload } from 'react-icons/hi';
import { legend_configuration } from '../style-configurations';
import { exportCsv } from '../../../utilities/common';
import { useTranslation } from 'react-i18next';
import fZHeiFontSrc from '../../../assets/fonts/FZHei-B01-Regular.font';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { formatNumber } from '../reportHelper';

const Download = ({
  dataRef,
  chartRef,
  options = ['csv'],
  reportName = 'Report',
  reportSubTitle,
  isCanvas = true,
  notScroll = false,
  exportDataHandler = null,
  disabled,
  locale,
}) => {
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = useState(false);
  const dropdownItems = [];
  options.forEach((o, idx) => {
    switch (o) {
      case 'csv':
        dropdownItems.push(<Dropdown.Item key={`dwl-${idx}`} onClick={() => handleDownload(o)}>CSV</Dropdown.Item>);
        break;
      case 'pdf':
        dropdownItems.push(<Dropdown.Item key={`dwl-${idx}`} onClick={() => handleDownload(o)}>PDF</Dropdown.Item>);
        break;
      case 'png':
        dropdownItems.push(<Dropdown.Item key={`dwl-${idx}`} onClick={() => handleDownload(o)}>PNG</Dropdown.Item>);
        break;
      case 'jpeg':
        dropdownItems.push(<Dropdown.Item key={`dwl-${idx}`} onClick={() => handleDownload(o)}>JPEG</Dropdown.Item>);
        break;
      default:
    }
  });

  const imageGenerator = async (input) => {
    const canvas = await html2canvas(input, { scale: 1 });
    return canvas.toDataURL('image/png', 1.0);
  };

  const loadLocaleFont = async () => {
    const loadFont = async (fontSrc) => {
      const res = await fetch(fontSrc);
      const font = await res.text();
      return font;
    };
    switch (locale) {
      case 'zh_CN':
        const content = await loadFont(fZHeiFontSrc);
        return {
          name: 'FZHei-B01T',
          type: 'normal',
          content,
        };
      default:
        return null;
    }
  };

  const handleDownload = async (option) => {
    try {
      setIsLoading(true);
      await processDownload(option);
    } catch (e) {
      console.log(e);
      toast.error(t('Something went wrong'));
    } finally {
      setIsLoading(false);
    }
  };

  const createHiddenContainer = (pageWidth) => {
    const wrapper = document.createElement('div');
    wrapper.style.position = 'absolute';
    wrapper.style.top = '-10000px';
    wrapper.style.width = `${pageWidth}px`;
    return wrapper;
  };

  const convertToCanvas = async (doc) => {
    const header = document.querySelector('.report-header') || { style: {} };
    header.style.display = 'block';
    const canvasPromise = html2canvas(doc, { scale: 2 });
    header.style.display = 'none';
    return await canvasPromise;
  };

  const createReportTitleImage = async (pageWidth) => {
    const wrapper = createHiddenContainer(pageWidth);
    const title = document.createElement('h1');
    title.innerText = reportSubTitle;
    title.style.fontSize = '18px';
    title.style.float = 'left';
    wrapper.appendChild(title);
    let image;
    try {
      document.body.appendChild(wrapper);
      image = await imageGenerator(title);
    } finally {
      wrapper.remove();
    }
    return image;
  };

  const createTableImage = (pdf, tableData) => {
    const [headerRow, ...bodyRows] = tableData;
    autoTable(pdf, {
      head: [headerRow],
      body: bodyRows,
      theme: 'grid',
      styles: {
        fontStyle: 'normal',
        fontSize: 8,
        textColor: [46, 53, 57],
        lineColor: [225, 225, 225],
        lineWidth: 0.6,
        overflow: 'linebreak',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [243, 243, 245],
        textColor: [31, 37, 57],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
    });
  };

  const processDownload = async (option) => {
    let image;
    let anchor;
    let ctx;
    let canvas;

    if (!!isCanvas) {
      let doc = document.getElementById(dataRef.current.id);
      if (!notScroll) doc.querySelector('.chart-legend').style['height'] = 'auto';

      switch (option) {
        case 'csv':
          let csvRows = exportDataHandler ? exportDataHandler('csv') : null;
          if (!csvRows) {
            const { legendItems } = chartRef.current.chartInstance.legend;
            const chartData = chartRef.current.props.data.datasets.filter(
              (dataset) => legendItems.find(item => item.text === dataset.label)?.hidden !== true
            );
            csvRows = [['', t('Total'), t('Unit'), ...chartRef.current.props.data.labels,]];
            chartData.forEach((dataset) => {
              const { title, total, displayUnit } = dataset;
              csvRows.push([
                title,
                formatNumber(total),
                displayUnit,
                ...dataset.data.map(num => formatNumber(num))
              ]);
            });
          }
          exportCsv(csvRows, reportName);
          if (!notScroll) doc.querySelector('.chart-legend').style['height'] = legend_configuration.chart_legend_height;
          break;
        case 'pdf':
          const pdf = new jsPDF({ orientation: 'p', unit: 'pt', format: 'a4' });
          const width = pdf.internal.pageSize.getWidth();
          const exportData = exportDataHandler ? exportDataHandler('pdf') : null;
          if (exportData) {
            const chartCanvas = await html2canvas(doc, { scale: 1.5 });
            const chartImg = chartCanvas.toDataURL('image/jpeg', 1);
            const margin = 20;
            let yPos = margin;
            if (reportSubTitle) {
              pdf.setFontSize(14);
              pdf.setTextColor(46, 53, 57);
              pdf.text(reportSubTitle, margin, yPos + 10);
              yPos += 30;
            }
            const imgProps = pdf.getImageProperties(chartImg);
            const pdfHeight = (imgProps.height * (width - margin * 2)) / imgProps.width;
            pdf.addImage(chartImg, 'jpeg', margin, yPos, width - margin * 2, pdfHeight);
            pdf.addPage();
            createTableImage(pdf, exportData);
            pdf.save(`${reportName}.pdf`);
          } else {
            const chartCanvas = await html2canvas(doc, { scale: 1.5 });
            const pdfImage = chartCanvas.toDataURL('image/jpeg', 1);
            const titleImage = await createReportTitleImage(width);
            pdf.addImage(titleImage, 'png', 20, 10);
            const imgProps = pdf.getImageProperties(pdfImage);
            const pdfHeight = (imgProps.height * (width - 40)) / imgProps.width;
            pdf.addImage(pdfImage, 'png', 20, 50, width - 40, pdfHeight);
            pdf.save(`${reportName}.pdf`);
          }
          break;
        case 'png':
        case 'jpeg':
          canvas = await convertToCanvas(doc);
          image = canvas.toDataURL(`image/${option}`, 1.0).replace(`image/${option}`, 'image/octet-stream');
          anchor = document.createElement('a');
          document.body.appendChild(anchor);
          anchor.setAttribute('download', `${reportName}.${option}`);
          anchor.setAttribute('href', image);
          anchor.click();
          anchor.parentNode.removeChild(anchor);
          if (!notScroll)
            doc.querySelector('.chart-legend').style['height'] = legend_configuration.chart_legend_height;
          break;
        case 'svg':
          break;
        default:
      }
    } else {
      let source = document.getElementById(chartRef.current.id);
      const rows = [].slice.call(chartRef.current.rows).map((row) => row.innerText.split('\t'));
      switch (option) {
        case 'csv':
          exportCsv(rows, reportName);
          break;
        case 'pdf':
          const pdf = new jsPDF({ orientation: 'l', unit: 'pt', format: 'a4' });
          const font = await loadLocaleFont();
          const defaultStyle = {};
          if (font) {
            pdf.addFileToVFS(`${font.name}.ttf`, font.content);
            pdf.addFont(`${font.name}.ttf`, font.name, font.type);
            pdf.setFont(font.name);
            defaultStyle.font = font.name;
          }
          autoTable(pdf, {
            html: source,
            styles: {
              ...defaultStyle,
              fontStyle: 'normal',
            },
            headStyles: {
              lineWidth: 0.25,
              lineColor: 200,
              fontSize: 9,
              fontStyle: 'normal',
            },
            bodyStyles: {
              lineWidth: 0.25,
              lineColor: 200,
              fontSize: 8
            },
            didDrawPage: function (data) {
              pdf.setFontSize(12);
              pdf.setTextColor(40);
              pdf.text(reportSubTitle, pdf.internal.pageSize.getWidth() / 2, 20, {
                align: 'center'
              });
            }
          });
          pdf.save(`${reportName}.pdf`);
          break;
        case 'png':
        case 'jpeg':
          source.classList.remove('responsive-table');
          canvas = await convertToCanvas(source);
          ctx = canvas.getContext('2d');
          ctx.globalCompositeOperation = 'destination-over';
          ctx.fillStyle = 'white';
          ctx.fillRect(0, 0, canvas.width, canvas.height);
          image = canvas.toDataURL('image/jpeg', 1.0).replace(`image/${option}`, 'image/octet-stream');
          anchor = document.createElement('a');
          document.body.appendChild(anchor);
          anchor.setAttribute('download', `${reportName}.${option}`);
          anchor.setAttribute('href', image);
          anchor.click();
          anchor.parentNode.removeChild(anchor);
          source.classList.add('responsive-table');
          break;
        case 'svg':
          break;
        default:
      }
    }
  };

  return (
    <Dropdown className="ml-3">
      <Dropdown.Toggle
        variant="secondary"
        className="d-flex align-items-center justify-content-center"
        disabled={!chartRef || isLoading || disabled}
      >
        <div className="mr-3">
          <HiDownload fontSize={17} />
        </div>
        {t('Download As')}
      </Dropdown.Toggle>
      <Dropdown.Menu>{dropdownItems}</Dropdown.Menu>
    </Dropdown>
  );
};

export default Download;
